import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { StatusBadge } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { Search, BookOpen, ChevronDown } from "lucide-react";

const STATUS_OPTS = ["All Modules", "Published", "In Progress", "Not Started"];

export default function LearningModules() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Modules");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [data, setData] = useState({ total: 20, modules: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({ search, status, lang: i18n.resolvedLanguage || "en" });
    api.get(`/teacher/modules?${params}`).then((r) => setData(r.data));
  }, [search, status, i18n.resolvedLanguage]);

  const visibleModules = data.modules.filter((m) => {
    const matchGrade = gradeFilter === "all" || (m.grade || "") === gradeFilter;
    const matchSubject = subjectFilter === "all" || m.subject === subjectFilter;
    return matchGrade && matchSubject;
  });

  return (
    <AppShell role="teacher">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Chapters</h1>
        <p className="mt-1 text-slate-500">Explore all chapters available for your class.</p>
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-card">
        <div className="flex items-center gap-3 text-slate-700">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
            <BookOpen size={18} />
          </div>
          <div>
            <p className="text-xs text-slate-500">Total Chapters</p>
            <p className="text-xl font-bold text-slate-900">{data.total}</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="filter-status-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
          >
            Filter by Status: <span className="text-slate-700">{status}</span> <ChevronDown size={14} />
          </button>
          {open && (
            <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
              {STATUS_OPTS.map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatus(s); setOpen(false); }}
                  data-testid={`filter-status-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 ${s === status ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-modules"
              placeholder="Search chapters…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <select data-testid="filter-grade" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
            <option value="all">All Grades</option>
            <option value="8">Grade 8</option>
            <option value="9">Grade 9</option>
            <option value="10">Grade 10</option>
            <option value="">Unassigned</option>
          </select>
          <select data-testid="filter-subject" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
            <option value="all">All Subjects</option>
            <option value="Science">Science</option>
            <option value="Maths">Maths</option>
            <option value="Maths - Ganita Prakash I">Maths - Ganita Prakash I</option>
            <option value="Maths - Ganita Prakash II">Maths - Ganita Prakash II</option>
            <option value="Maths - Ganita Manjari I">Maths - Ganita Manjari I</option>
            <option value="Maths - Ganita Manjari II">Maths - Ganita Manjari II</option>
          </select>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">Chapter Name</th>
                <th className="py-3 pr-4">Objectives</th>
                <th className="py-3 pr-4">Learning Outcomes</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Estimated Time</th>
              </tr>
            </thead>
            <tbody>
              {visibleModules.map((m, i) => (
                <tr
                  key={m.id}
                  onClick={() => navigate(`/teacher/modules/${m.id}`)}
                  data-testid={`teacher-module-row-${m.id}`}
                  className="cursor-pointer border-b border-slate-50 align-top transition-colors hover:bg-slate-50/60"
                >
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{m.name} <span className="ml-1 text-xs font-normal text-slate-500">({m.subject})</span></td>
                  <td className="py-4 pr-4 text-slate-600">
                    <ul className="list-disc space-y-0.5 pl-4">
                      {m.objectives.map((o, k) => <li key={k}>{o}</li>)}
                    </ul>
                  </td>
                  <td className="py-4 pr-4 text-slate-600">
                    <ul className="list-disc space-y-0.5 pl-4">
                      {m.outcomes.map((o, k) => <li key={k}>{o}</li>)}
                    </ul>
                  </td>
                  <td className="py-4 pr-4"><StatusBadge value={m.teacher_status} /></td>
                  <td className="py-4 pr-4 text-slate-600">{m.estimated_time}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleModules.length === 0 && <p className="py-12 text-center text-sm text-slate-500">No chapters match.</p>}
        </div>
      </div>
    </AppShell>
  );
}
